import {UIProperties} from "@/engine/UI/UIProperties.js";
import {UITemplates} from "@/engine/UITemplates.js";

class UIComponent {
    constructor(options, view) {
        this.id = ''
        // view model
        // UIProperties || UIComponent || template name || function
        this.view = view
        // could be used by a parent component (like dialog) to display title
        // can be string or function
        this.title = null
        // could be used by a parent component to display an icon
        this.iconBig = null
        this.iconSmall = null
        // assigns css class name to the container
        this.className = ''
        // usually is used by a parent component (like dialogs)
        // to get names of button and etc
        this.mainActionName = null
        this.cancelActionName = null
        // content data that will be inserted into the view
        // can be string, or an object, or function
        this.contentData = null
        // triggers to return an empty content if contentData is null or empty
        this.emptyIfNoContentData = false
        // content could contain elements with data-bind attribute
        // this elementLookupAttributeName is used to get access
        // to these elements using el() method
        this.elementLookupAttributeName = 'data-bind'
        // element tags to lookup
        this.lookupElements = ["input", "select", "span", "li"]
        // calls the parent container close method on UIC close
        this.callContainerClose = false
        // storage where to save or load the component context data
        // should be used to persist the UI state
        this.contextStorage = null
        // the key name to store context data
        this.contextStorageKey = null
        // callbacks
        this.beforeOpen = []
        this.afterOpen = []
        this.beforeClose = []
        this.afterClose = []
        this.containerElement = null
        this.container = null
        this._content = null
        this.parent = null
        this.debug = false
        //this.children = {}
        if (options) {
            Object.assign(this, options)
        }
    }

    open(container, parent, data, templateIncludes) {
        if (container && this.containerElement && container !== this.containerElement) {
            console.log(`UIC: warn: ${this.id}: open is called twice, check it`)
        }

        if (! container) {
            console.log(`UIC: warn: ${this.id}: open with empty container`)
            return
        }

        if (this.debug)
            console.log(`UIC: ${this.id}: open`)

        // already opened
        if (this.containerElement) {
            this.updateContent()
        }

        if (parent)
            this.parent = parent

        if (container instanceof L.Control.Window) {
            this.containerElement = container.getContainer()
        } else {
            this.containerElement = container
        }
        this.container = container

        this._setContent(this.initContent(data, templateIncludes))

        if (this.className !== '') {
            this.containerElement.classList.add(this.className);
        }
    }

    /**
     * closes and removes a component content
     * onCancel is a boolean that usually means was the component canceled
     * @param result
     */
    close(onCancel = false) {
        if (!this.containerElement)
            return
        if (this.debug)
            console.log(`UIC: ${this.id}: close`)
        this._run(this.beforeClose)
        this._run(this.afterClose)
        this.containerElement.innerHTML=""
        if (this.className !== '') {
            this.containerElement.classList.remove(this.className);
        }
        this.containerElement = null
        if (this.container instanceof L.Control.Window && this.callContainerClose) {
            this.container.close("ok") // some bug, we use "ok" button as cancel button
        }
        // notify parent control about closing this component
        if (this.parent && this.parent.childCloseEvent) {
            this.parent.childCloseEvent(this, onCancel)
        }
    }

    // initContent creates DOM fragment filled with contentData
    // this.view is used as a view model and could be:
    //  tamplate name, UIProperties, UIComponent, function
    //  contentData can be string, an object, or function
    //  if contentData is an object it is used as parameters struct
    //  in template
    // initContent could return string or HTMLElement or Node list
    initContent(contentData, templateIncludes) {
        if (!contentData) {
            if (typeof this.contentData === "function") {
                contentData = this.contentData()
                // contentData() could return scalar or array of 2 elements
                if (Array.isArray(contentData)) {
                    if (contentData.length > 1) {
                        templateIncludes = contentData[1]
                        contentData = contentData[0]
                    }
                }
            } else {
                contentData = this.contentData
            }
        }

        if (typeof this.view === "function") {
            this._content = this.view(contentData)
            return this._content
        }

        if (this.view instanceof UIProperties) {
            this._content = this.view.content()
            return this._content
        }

        if (this._content)
            return this._content

        // an object, possible HTML element or collection
        if (typeof this.view !== "string") {
            this._content = this.view
            return this._content
        }

        if (this.emptyIfNoContentData) {
            if (! contentData ||
                (typeof contentData === "object" && Object.keys(contentData).length === 0)) {
                 this._content = ""
                 return this._content
            }
        }

        if (! contentData || typeof contentData !== "object") {
            this._content = this.template(this.view, contentData, templateIncludes)
            return this._content
        }

        // contentData could contain child UIComponent,
        // here we create the list of these components to init then later,
        // than we init general content from template and after
        // init all included components and insert their content
        // into the template at place of element with id where this component key was located
        let inclComponents = {}
        let data = this._deepClone(contentData, 0,function (obj) {
            if (obj instanceof UIComponent) {
                // generate the id of an element to search it later
                let id = Math.floor(Math.random() * 10000)
                inclComponents[id] = obj
                return this.id + "-" + id
            } else {
                return null
            }
        }.bind(this))

        this._content = this.template(this.view, data, templateIncludes)
        const range = document.createRange();
        const fragment = range.createContextualFragment(this._content);
        for (let id in inclComponents) {
            const el = fragment.getElementById(this.id + "-" + id)
            if (el) {
                inclComponents[id].open(el, this)
            }
        }

        if (fragment.childElementCount === 1) {
            return fragment.firstElementChild
        } else {
            return fragment.children
        }
    }

    _deepClone(obj, depth = 0, filter) {
        if (typeof obj !== 'object')
            return obj;

        if (filter && typeof filter === "function") {
            let o = filter(obj)
            if (o)
                return o
        }

        if (depth === 3)
            return obj

        let data
        if (Array.isArray(obj)) {
            data = obj.slice()
        } else {
            data = Object.assign({}, obj)
        }
        for (let key in data) {
            data[key] = this._deepClone(data[key], depth + 1, filter);
        }
        return data
    }

    template(name, data, includes) {
        return UITemplates.getContent(name, data, includes)
    }

    updateContent(contentData, templateIncludes) {
        this._content = null
        // no container, possible UIC wasn't opened, or closed
        if (!this.containerElement) {
            console.log(`UIC: warn: ${this.id}: updateContent is called but UIC isn't in opened state`)
            return
        } else {
            if (this.debug)
                console.log(`UIC: ${this.id}: updateContent`)
        }
        this._setContent(this.initContent(contentData, templateIncludes))
        // notify parent control about this component were updated
        if (this.parent && this.parent.childRefreshEvent) {
            this.parent.childRefreshEvent(this)
        }
    }

    getTitle() {
        if (this.title && typeof this.title === "function") {
            return this.title()
        }
        return this.title
    }
    _setContent(content) {
        if (typeof content === "string") {
            this.containerElement.innerHTML = content
        } else if (content instanceof NodeList ) {
            this.containerElement.innerHTML = ""
            this.containerElement.append(...Array.from(content.values()))
        } else if (content instanceof HTMLCollection) {
            this.containerElement.innerHTML = ""
            while (content.length > 0) {
                this.containerElement.appendChild(content[0]);
            }
        } else if (content instanceof UIComponent ) {
            this.containerElement.innerHTML = ""
            content.open(this.containerElement, this)
            //this.children[content.id] = content
        } else if (content instanceof HTMLElement ) {
            this.containerElement.innerHTML = ""
            this.containerElement.append(content)
        } else {
            console.log()
        }
        this.onContentReady(this.containerElement)
    }

    _run(list, data) {
        for (let c of list) {
            if (typeof c === "function")
                c(data)
        }
    }

    _elSelector(name) {
        let lookupElements = ["input", "select", "span", "li"]
        let selector
        if (name) {
            selector = this.lookupElements.map( e => `${e}[${this.elementLookupAttributeName}='${name}']`)
        } else {
            selector = this.lookupElements.map( e => `${e}[${this.elementLookupAttributeName}]`)
        }
        return selector
    }

    // el is a helping method
    // returns an element from content container by name
    el(name, subContainerSelector) {
        let container = subContainerSelector ?
            this.containerElement.querySelector(subContainerSelector) :
            this.containerElement
        return container.querySelector(this._elSelector(name).join(", "))
    }

    // els is a helping method
    // returns a collection of elements from content container by name
    els(name, subContainerSelector) {
        let container = subContainerSelector ?
            this.containerElement.querySelector(subContainerSelector) :
            this.containerElement
        if (!container)
            return []
        return container.querySelectorAll(this._elSelector(name).join(", "))
    }

    // calls from parent on main action (usually the dialog "OK" button)
    onMainAction() {

    }

    // calls from parent on cancel action
    // (usually the dialog "Cancel" button) or parent destroy
    onCancelAction() {
        this.close(true)
        return true
    }

    // calls from a child when data refreshed
    childRefreshEvent(uic) {

    }
    // onContentReady calls when content is created
    // and inserted into the container element
    // could be used to preform some content postprocessing
    // for example set event handlers, field values, etc...
    onContentReady(container) {

    }

    // should return false if something wrong with data and component couldn't be opened
    validateBeforeOpen() {
        return true
    }

    saveContext(ctx) {
        if (this.contextStorage && this.contextStorageKey) {
            this.contextStorage.set({[this.contextStorageKey]: ctx})
        }
    }

    loadContext(restoreCb) {
        if (! this.contextStorage ||
            ! this.contextStorageKey ||
            ! restoreCb)
            return
        restoreCb(this.contextStorage.get(this.contextStorageKey))
    }
}

export {UIComponent};
